import React, { createContext, useContext, useState, useEffect } from 'react';
import { settingsAPI } from '@/services';
import { ORGANIZATION_DISPLAY_NAME, applyDocumentBranding } from '@/config/branding';
import { syncProjectPortalToDocument } from '@/utils/portalBranding';
import { normalizeUiSurfaces } from '@/utils/uiSurfaceVisibility';
import { useAuth } from './AuthContext';

const SettingsContext = createContext();

const DEFAULT_ASSISTANCE_SETTINGS = {
  default_case_amount: 0,
  governance: {
    fund_type: 'solidarity',
    eligibility_rules: '',
    contribution_periodicity: 'monthly',
    covered_event_categories: ['deces', 'hospitalisation'],
    category_thresholds: [
      { category: 'deces', min_amount: 0, max_amount: 0 },
      { category: 'hospitalisation', min_amount: 0, max_amount: 0 },
    ],
    assistance_min_amount: 0,
    assistance_max_amount: 0,
  },
  member_lifecycle: {
    membership_mode: 'open',
    active_statuses: ['active', 'suspended'],
    minimum_seniority_months: 0,
    contribution_situation_mode: 'auto',
    track_benefit_history: true,
  },
  financial_mechanics: {
    expected_contribution_amount: 0,
    exceptional_contribution_enabled: false,
    exceptional_contribution_label: 'Contribution exceptionnelle',
    exceptional_contribution_types: [],
    allow_arrears_tracking: true,
  },
  case_workflow: {
    require_reason: true,
    require_decision_note: true,
    close_after_days: 0,
  },
  transparency: {
    reports_frequency: 'monthly',
    enable_aid_summary: true,
    enable_contributions_summary: true,
    enable_case_tracking: true,
  },
};

const DEFAULT_ORGANIZATION = {
  name: ORGANIZATION_DISPLAY_NAME,
  full_name: '',
  registration_number: '',
  tax_id: '',
  address: '',
  phone: '',
  email: '',
  website: '',
  logo_url: '',
  description: '',
  support_email: '',
  portal_meta_description: '',
};

function mergeOrganization(patch) {
  const o = { ...DEFAULT_ORGANIZATION, ...(patch && typeof patch === 'object' ? patch : {}) };
  o.name = (o.name && String(o.name).trim()) || ORGANIZATION_DISPLAY_NAME;
  return o;
}

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

export const SettingsProvider = ({ children }) => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    const hasProjectContext = !!localStorage.getItem('project_id');
    if (!isAuthenticated || !hasProjectContext) {
      setSettings({
        organization: mergeOrganization({}),
        project_portal: {
          name: '',
          site_description: '',
          support_email: '',
          public_domain: '',
        },
        bank_accounts: [],
        signatories: [],
        assistance: DEFAULT_ASSISTANCE_SETTINGS,
        system: { ui_surfaces: normalizeUiSurfaces(null) },
      });
      setLoading(false);
      return;
    }
    loadSettings();
  }, [isAuthenticated, authLoading]);

  const loadSettings = async () => {
    try {
      const response = await settingsAPI.getAll();
      // Ensure arrays are always defined
      const data = response.data || {};
      const defaultPortal = {
        name: '',
        site_description: '',
        support_email: '',
        public_domain: '',
      };
      setSettings({
        ...data,
        bank_accounts: [],
        signatories: data.signatories || [],
        organization: mergeOrganization(data.organization),
        project_portal:
          typeof data.project_portal === 'object' && data.project_portal
            ? { ...defaultPortal, ...data.project_portal }
            : defaultPortal,
        system: {
          ...(data.system || {}),
          ui_surfaces: normalizeUiSurfaces(data.system?.ui_surfaces),
        },
        events: data.events || {
          event_types: ['meeting', 'deadline', 'event'],
          meeting_modes: ['présentiel', 'en ligne', 'hybride'],
          default_event_type: 'meeting',
          default_meeting_mode: 'présentiel'
        },
        assistance: {
          ...DEFAULT_ASSISTANCE_SETTINGS,
          ...(data.assistance || {}),
          governance: {
            ...DEFAULT_ASSISTANCE_SETTINGS.governance,
            ...(data.assistance?.governance || {}),
          },
          member_lifecycle: {
            ...DEFAULT_ASSISTANCE_SETTINGS.member_lifecycle,
            ...(data.assistance?.member_lifecycle || {}),
          },
          financial_mechanics: {
            ...DEFAULT_ASSISTANCE_SETTINGS.financial_mechanics,
            ...(data.assistance?.financial_mechanics || {}),
          },
          case_workflow: {
            ...DEFAULT_ASSISTANCE_SETTINGS.case_workflow,
            ...(data.assistance?.case_workflow || {}),
          },
          transparency: {
            ...DEFAULT_ASSISTANCE_SETTINGS.transparency,
            ...(data.assistance?.transparency || {}),
          },
        },
      });
    } catch (err) {
      console.error('Failed to load settings:', err);
      // Use default settings if load fails
      setSettings({
        organization: mergeOrganization({}),
        project_portal: {
          name: '',
          site_description: '',
          support_email: '',
          public_domain: '',
        },
        bank_accounts: [],
        signatories: [],
        assistance: DEFAULT_ASSISTANCE_SETTINGS,
        system: { ui_surfaces: normalizeUiSurfaces(null) },
      });
    } finally {
      setLoading(false);
    }
  };

  const refreshSettings = () => {
    loadSettings();
  };

  useEffect(() => {
    if (authLoading || loading) return;
    if (!isAuthenticated || !localStorage.getItem('project_id')) {
      applyDocumentBranding();
      return;
    }
    if (settings?.organization || settings?.project_portal) {
      syncProjectPortalToDocument(settings);
    }
  }, [
    authLoading,
    loading,
    isAuthenticated,
    settings?.organization?.name,
    settings?.organization?.email,
    settings?.organization?.support_email,
    settings?.organization?.description,
    settings?.organization?.portal_meta_description,
    settings?.project_portal?.name,
    settings?.project_portal?.site_description,
    settings?.project_portal?.support_email,
    settings?.project_portal?.public_domain,
  ]);

  return (
    <SettingsContext.Provider value={{ settings, loading, refreshSettings }}>
      {children}
    </SettingsContext.Provider>
  );
};

